import React, { SyntheticEvent, useCallback, useMemo } from 'react';

import { Autocomplete, Stack, TextField } from '@mui/material';

import { CommissionOption, RuleCondition } from '~/consts/agentHub';

import { useGetCruiseLines } from '~/hooks/apis/cruises/useGetCruiseLines';

import { type CruiseLine } from '~/services/cruises/CruiseLineService';

type CruiseLineOption = {
  value: string;
  label: string;
  code?: string;
};

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function cruiseLineToOption(cruiseLine: CruiseLine): CruiseLineOption {
  return {
    value: cruiseLine.name,
    label: cruiseLine.name,
    code: cruiseLine.code,
  };
}

function conditionToOption(condition: CommissionOption[number]): CruiseLineOption {
  return {
    value: String(condition.value),
    label: String(condition.label || condition.value),
  };
}

function optionToCondition(option: CruiseLineOption): CommissionOption[number] {
  return {
    value: option.value,
    label: option.label,
  };
}

function getCruiseLineOptionLabel(option: CruiseLineOption) {
  return option.code ? `${option.label} (${option.code})` : option.label;
}

export default function AgentHubCommissionsCruiseLineInput({ setCreatedConditions, existingConditions }: Props) {
  const { data: cruiseLines = [], isFetching, isError } = useGetCruiseLines();

  const selectedCruiseLines = useMemo(() => existingConditions.map(conditionToOption), [existingConditions]);

  const options = useMemo(() => {
    const byValue = new Map<string, CruiseLineOption>();

    for (const cruiseLine of cruiseLines) {
      const option = cruiseLineToOption(cruiseLine);
      byValue.set(option.value, option);
    }

    for (const selectedCruiseLine of selectedCruiseLines) {
      if (!byValue.has(selectedCruiseLine.value)) {
        byValue.set(selectedCruiseLine.value, selectedCruiseLine);
      }
    }

    return Array.from(byValue.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  }, [cruiseLines, selectedCruiseLines]);

  const handleChange = useCallback(
    (_: SyntheticEvent, next: Array<CruiseLineOption>) => {
      setCreatedConditions(
        {
          type: 'cruiseLine',
          value: next.map(optionToCondition),
        },
        true,
      );
    },
    [setCreatedConditions],
  );

  const noOptionsText = useMemo(() => {
    if (isError) {
      return 'Failed to fetch cruise lines';
    }
    if (isFetching) {
      return 'Searching...';
    }
    return 'No cruise lines found';
  }, [isError, isFetching]);

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        loading={isFetching}
        options={options}
        getOptionLabel={getCruiseLineOptionLabel}
        filterSelectedOptions
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Cruise lines"
            placeholder={selectedCruiseLines.length > 0 ? '' : 'Cruise lines'}
          />
        )}
        value={selectedCruiseLines}
        onChange={handleChange}
        noOptionsText={noOptionsText}
      />
    </Stack>
  );
}
